import { useState, useEffect, useMemo, useRef } from 'react';
import Field from './Field';
import api from '../services/api';
import { createSurpriseGift } from '../services/surpriseGifts';

/**
 * SurpriseGiftModal — add a wish ON BEHALF of another group member, hidden from
 * them but visible (and giftable) to the rest of the group. The secret cousin of
 * SuggestIdeaModal: a suggestion is consensual and recipient-aware; a surprise
 * gift must NEVER reach the recipient (the API enforces this with a global query
 * scope — see FEEDBACK_PLAN.md §5). Cloned from WishFormModal / SuggestIdeaModal:
 * fixed-overlay markup, per-field useState, a `saving` flag, an inline `error`,
 * and onClose/onSaved props.
 *
 * The recipient is FIXED (passed in, not chosen here) — every entry point already
 * knows who the surprise is for. The group picker is limited to groups the
 * recipient actually shares with you, so we can never submit a recipient/group
 * mismatch (the API would 422).
 *
 * Props:
 *  - recipient: { id, name } — who the surprise is FOR (required).
 *  - prefill:   optional starting values: { groupId }.
 *  - onClose:   close without saving.
 *  - onSaved:   called with the created wish after a successful save.
 *  - notify:    the host page's flash helper, notify(type, text) (optional).
 */

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

export default function SurpriseGiftModal({ recipient, prefill = {}, onClose, onSaved, notify }) {
    const recipientName = recipient?.name || 'they';

    // Reference data.
    const [groups, setGroups] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loadingRefs, setLoadingRefs] = useState(true);

    // Form fields.
    const [groupId, setGroupId] = useState(prefill.groupId ? String(prefill.groupId) : '');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [url, setUrl] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [priceMin, setPriceMin] = useState('');
    const [priceMax, setPriceMax] = useState('');
    const [priority, setPriority] = useState('medium');
    const [categoryId, setCategoryId] = useState('');

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Product-image scrape from a pasted URL (same Open Graph fetch as WishFormModal).
    const [fetchingMeta, setFetchingMeta] = useState(false);
    const [metaNote, setMetaNote] = useState(null); // { warn, text }
    const lastFetchedUrl = useRef('');

    // Load groups (with members) + categories once. Only groups the RECIPIENT is
    // also a member of can hold their surprise, so we narrow the picker to those.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [groupsRes, catsRes] = await Promise.all([
                    api.get('/groups'),
                    api.get('/wish-categories'),
                ]);
                if (cancelled) return;
                const shared = (groupsRes.data || []).filter((g) =>
                    (g.members || []).some((m) => m.id === recipient?.id)
                );
                setGroups(shared);
                setCategories(catsRes.data);
                // Keep the prefilled group only if it's one we share with them;
                // otherwise default to the first shared group.
                setGroupId((prev) => {
                    if (prev && shared.some((g) => String(g.id) === prev)) return prev;
                    return shared.length ? String(shared[0].id) : '';
                });
            } catch (err) {
                if (!cancelled) setError(errText(err, 'Could not load your groups.'));
            } finally {
                if (!cancelled) setLoadingRefs(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // Load once on mount; recipient is stable for the modal's lifetime.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sharesNoGroup = !loadingRefs && groups.length === 0;

    const selectedGroupName = useMemo(
        () => groups.find((g) => String(g.id) === String(groupId))?.name || '',
        [groups, groupId]
    );

    const fetchMeta = async (raw) => {
        const u = (raw || '').trim();
        if (!/^https?:\/\/.+/i.test(u) || u === lastFetchedUrl.current) return;
        lastFetchedUrl.current = u;
        setFetchingMeta(true);
        setMetaNote(null);
        try {
            const res = await api.get('/url-metadata', { params: { url: u } });
            if (res.data.image) setImageUrl((prev) => prev || res.data.image);
            // Only fill the title if the user hasn't typed one.
            setTitle((prev) => (prev.trim() ? prev : res.data.title || prev));
            if (!res.data.image) {
                setMetaNote({ warn: true, text: "Couldn't find a product image for that link — paste one below." });
            }
        } catch (err) {
            setMetaNote({ warn: true, text: "Couldn't read that link — you can paste an image URL below." });
        } finally {
            setFetchingMeta(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;
        setError('');

        if (!recipient?.id) {
            setError('No recipient — close and try again.');
            return;
        }
        if (!groupId) {
            setError('Pick a group you share with them.');
            return;
        }
        if (!title.trim()) {
            setError('Give the gift a title.');
            return;
        }

        const payload = {
            recipient_user_id: recipient.id,
            group_id: Number(groupId),
            title: title.trim(),
            description: description.trim() || null,
            url: url.trim() || null,
            image_url: imageUrl.trim() || null,
            price_min: priceMin === '' ? null : priceMin,
            price_max: priceMax === '' ? null : priceMax,
            priority: priority || null,
            category_id: categoryId === '' ? null : Number(categoryId),
        };

        setSaving(true);
        try {
            const created = await createSurpriseGift(payload);
            if (notify) notify('success', `Surprise added — ${recipientName} won't see it.`);
            onSaved?.(created);
        } catch (err) {
            setError(errText(err, 'Could not add the surprise gift.'));
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full my-8">
                <h3 className="text-lg font-semibold mb-1">Add a surprise gift 🎁</h3>
                <p className="text-sm text-gray-500 mb-4">
                    Add a wish for <span className="font-medium text-gray-700">{recipientName}</span>. Only the group
                    will see this — {recipientName} won't.
                </p>

                {loadingRefs ? (
                    <div className="text-center py-8 text-gray-500 text-sm">Loading…</div>
                ) : sharesNoGroup ? (
                    <div className="space-y-4">
                        <p className="text-sm text-amber-600">
                            You don't share a group with {recipientName} yet — add them to a group first, then you can
                            surprise them.
                        </p>
                        <div className="flex justify-end">
                            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">
                                Close
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-3">
                        <Field label="Group" required>
                            <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className={inputCls}>
                                {groups.map((g) => (
                                    <option key={g.id} value={g.id}>
                                        {g.icon ? `${g.icon} ` : ''}{g.name}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-400 mt-1">
                                Everyone in {selectedGroupName || 'this group'} can see and gift it — except {recipientName}.
                            </p>
                        </Field>

                        <Field label="Title" required>
                            <input
                                type="text"
                                required
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className={inputCls}
                                placeholder="e.g., Wireless headphones"
                            />
                        </Field>

                        <Field label="Description">
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className={inputCls}
                                rows={2}
                                placeholder="Anything the group should know"
                            />
                        </Field>

                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Product URL">
                                <input
                                    type="url"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    onBlur={(e) => fetchMeta(e.target.value)}
                                    onPaste={(e) => {
                                        const text = e.clipboardData.getData('text');
                                        setTimeout(() => fetchMeta(text), 0);
                                    }}
                                    className={inputCls}
                                    placeholder="https://…"
                                />
                                <p className={`text-xs mt-1 ${metaNote?.warn ? 'text-amber-600' : 'text-gray-400'}`}>
                                    {fetchingMeta
                                        ? 'Fetching image from link…'
                                        : metaNote?.text || 'Paste a product link to auto-fill the image.'}
                                </p>
                            </Field>
                            <Field label="Image URL">
                                <input
                                    type="url"
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    className={inputCls}
                                    placeholder="https://…"
                                />
                                {imageUrl && (
                                    <img src={imageUrl} alt="" className="mt-2 h-16 w-16 object-cover rounded border border-gray-200" />
                                )}
                            </Field>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                            <Field label="Min price">
                                <input type="number" min="0" step="0.01" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} className={inputCls} />
                            </Field>
                            <Field label="Max price">
                                <input type="number" min="0" step="0.01" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} className={inputCls} />
                            </Field>
                            <Field label="Priority">
                                <select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputCls}>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </select>
                            </Field>
                        </div>

                        <Field label="Category">
                            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={inputCls}>
                                <option value="">— none —</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.icon ? `${c.icon} ` : ''}{c.name}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        {error && <p className="text-sm text-red-600">{error}</p>}

                        <div className="flex gap-2 justify-end pt-2">
                            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {saving ? 'Saving…' : 'Add surprise 🎁'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

const inputCls =
    'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

